import { useId } from "react";
import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { CONFLICT_LEVELS, type Review } from "../../../api/content/reviewDecoders";
import { rerunConflictCheck } from "../../../api/content/reviewsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import ConflictLevelBadge from "../page/ConflictLevelBadge";
import { allows } from "../state/decisionRules";
import ConflictRowView from "./ConflictRowView";
import { useReviewTermNames } from "./conflictTermNames";
import { conflictRows } from "./conflictRows";

/**
 * What the change conflicts with (FR-VERSION-5, requirements §6.10), grouped by level,
 * examples first. Each row names one connected item and the changed terms it relies on,
 * with what changed in them on a quiet second line.
 */
export default function ConflictsPanel({
  review,
  onFocusItem,
  onChanged,
}: {
  review: Review;
  onFocusItem: (itemId: string) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const rerun = useSubmission();
  const check = review.conflictCheck;
  const termNames = useReviewTermNames(review);
  const changedIds = new Set(review.items.map((item) => item.itemId));
  return (
    <section className="app-card space-y-3 p-3" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 id={headingId} className="app-title flex-1 text-base font-semibold">{t("reviews.conflicts.title")}</h3>
        {allows(review, "rerun_conflict_check") ? (
          <AppButton
            size="xs"
            variant="ghost"
            disabled={rerun.state.status === "submitting"}
            title={check !== null ? t("reviews.conflicts.rules", { version: check.rulesVersion }) : undefined}
            onClick={() => {
              rerun.submit(async () => {
                await rerunConflictCheck(review.changeSet.changeSetId);
                onChanged();
              });
            }}
          >
            <ArrowPathIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {t("reviews.conflicts.rerun")}
          </AppButton>
        ) : null}
      </div>
      {rerun.state.status === "failed" ? <ErrorNotice error={rerun.state.error} compact={true} /> : null}
      {check === null ? <p className="app-text-muted text-sm">{t("reviews.conflicts.notRun")}</p> : null}
      {check !== null && check.findings.length === 0 ? <p className="app-text-muted text-sm">{t("reviews.conflicts.none")}</p> : null}
      {check !== null
        ? CONFLICT_LEVELS.map((level) => {
            const rows = conflictRows(check.findings.filter((finding) => finding.level === level));
            return rows.length === 0 ? null : (
              <div key={level} className="space-y-1">
                <h4 className="flex items-center gap-2 text-sm font-semibold">
                  <ConflictLevelBadge level={level} />
                  {t(`reviews.levels.${level}Explained`, { count: rows.length })}
                </h4>
                <ul className="space-y-1">
                  {rows.map((row, index) => (
                    <ConflictRowView
                      key={row.kind === "item_relies_on" ? row.item.itemId : `single-${String(index)}`}
                      row={row}
                      labels={termNames}
                      changedItemIds={changedIds}
                      onFocusItem={onFocusItem}
                    />
                  ))}
                </ul>
              </div>
            );
          })
        : null}
    </section>
  );
}
